import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentRef,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EditSettingsModel,
  GridAllModule,
  GridComponent,
  RowSelectEventArgs,
  SelectionSettingsModel
} from '@syncfusion/ej2-angular-grids';
import {
  AccessType,
  ApiService,
  AppHelper,
  GridColumnDataHelper,
  IdbService,
  LocalStorageService,
  SessionStorageService,
  TabGetService,
  UserPersonalizationHelper,
  getPrimaryKeyFields,
} from '@techextensor/tab-core-utility';
import { isEmpty } from 'lodash';
import { firstValueFrom, Subject, switchMap, take, map, shareReplay } from 'rxjs';
import { ComponentType, FormioCustomComponentSelector, operatorMapping, PermissionType, ReportType, ScreenRenderingMode, TemplateContextKey } from '../../../../core/const/constants';
import { ComponentDataFetchConfig, CustomComponentValueSchema } from '../../../../core/interfaces/common.interface';
import { TreeGridAllModule } from '@syncfusion/ej2-angular-treegrid';
import { HelperFunctionService } from '../../../../core/services/helper-function.service';
import { MonacoContextService } from '../../../../core/services/monaco/monaco-context.service';
import { TabCustomviewComponent } from '../tab-customview/tab-customview.component';
import { EventActionService } from '../../../../core/services/event-action.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { PermissionService } from '../../../../core/services/permission.service';
import moment from 'moment';
import { ExtensionDependencies } from '../../../../core/const/extension-dependencies';
import { TabSdk } from '@techextensor/tab-sdk';
import { TranslationToken } from '../../../../core/const/translation.token';
import { ToastrService } from 'ngx-toastr';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { Tooltip } from '@syncfusion/ej2-angular-popups';
import { TabFormRendererComponent } from '../../tab-form-renderer/tab-form-renderer.component';
import { UserFavoritesService } from '../../../../core/services/user-favorites.service';
import { DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';

@Component({
  selector: 'app-tab-grid',
  imports: [GridAllModule, TreeGridAllModule, TabCustomviewComponent, DropDownButtonModule, CommonModule],
  templateUrl: './tab-grid.component.html',
  styleUrl: './tab-grid.component.scss'
})
export class TabGridComponent {
  @Input() public gridData: any;
  @Input() public isInDesigningMode: boolean = false;
  public treeColumnIndex: number = 0;
  private isInitialDataBound: boolean = true;
  public isShowGrid: boolean = false;
  public toolbarItems: any[] = [];
  public columnMenuItems: any[] = ['AutoFitAll', 'AutoFit'];
  public contextMenuItems: any = [];

  public exportItems: any[] = [];

  public editOptions: EditSettingsModel = {
    allowEditing: false,
    allowAdding: false,
    allowDeleting: false,
    allowEditOnDblClick: false,
    showDeleteConfirmDialog: true
  };
  public gridColumns: any;
  public selectionOptions: SelectionSettingsModel = { mode: 'Row', type: 'Single' };

  public isContextMenuOpen: boolean = false;
  public activeTooltipComponentRef: ComponentRef<TabFormRendererComponent> | null = null;

  public gridUniqueId: string = TabSdk.util.generateUniqueId('grid_');

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // ===== Angular 19 Reactive Data Fetching Pattern =====
  // Trigger for data fetching - emits configuration for each fetch request
  private readonly fetchTrigger$ = new Subject<ComponentDataFetchConfig>();

  // Reactive data pipeline with automatic request cancellation via switchMap
  private readonly gridDataResponse$ = this.fetchTrigger$.pipe(
    switchMap(config =>
      this.helperFunctionService.getDSQData(
        config.dsqId,
        config.reqtokens,
        config.params,
        false,
        config.headers
      ).pipe(
        take(1),
        map((res: any) => ({ res, initialLoad: config.initialLoad }))
      )
    ),
    shareReplay(1), // Cache the latest value
    takeUntilDestroyed(this.destroyRef) // Automatic cleanup on component destroy
  );

  public gridColumnHelper: GridColumnDataHelper = inject(GridColumnDataHelper);
  public tabGetService: TabGetService = inject(TabGetService);
  private readonly userPersonalizationHelper: UserPersonalizationHelper = inject(UserPersonalizationHelper);
  public _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  public helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private eventActionService: EventActionService = inject(EventActionService);
  public _router: Router = inject(Router);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly apphelper: AppHelper = inject(AppHelper);
  private readonly idbService: IdbService = inject(IdbService);
  private readonly _tabToasterService: ToastrService = inject(ToastrService);
  private readonly _sessionStorageService: SessionStorageService = inject(SessionStorageService);
  private readonly _localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly _userFavoritesService: UserFavoritesService = inject(UserFavoritesService);
  private readonly monacoContextService: MonacoContextService = inject(MonacoContextService);

  public userPersonalizeData: any;

  public searchSettings: any = { key: '' };
  public sortSettings: any = { columns: [] };
  public filterSettings: any = { type: 'Menu', columns: [] };
  public pageSettings: any = {};

  public currentGridExtension: any;

  public activeCustomViewId: any;
  public activeRow: any;

  private currentAppObject: any;
  public currentPrimaryKeyField: any;
  public gridHeight = '400px'; // CHANGED: from '100%' to '400px'
  private originalDimensions: any;

  private lookupCache = new Map<string, Map<any, string>>();

  public _value: CustomComponentValueSchema = {
    component: {},
    otherFormdata: {},
    componentValue: {},
    parameters: {}
  };

  private timeouts: any[] = [];

  // Static Data Mode Support - Getters for mode detection
  private get gridMode(): 'dsq' | 'static' {
    return this._value.component?.Configuration?.componentValue?.mode || 'dsq';
  }

  public get isStaticMode(): boolean {
    return this.gridMode === 'static';
  }

  @ViewChild('customViewTemplate', { static: true }) public customViewTemplate: any;
  @ViewChild('customViewInstance') public customViewInstance?: any;
  @ViewChild('extensionComponentRootInstance', { read: ViewContainerRef }) public extensionComponentRootInstance: any;
  @ViewChild('gridInstance') public gridInstance?: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('exportDropdownTemplate', { static: true }) public exportDropdownTemplate: any;

  @Output() valueChange = new EventEmitter<any>();
  @Output() formioEvent = new EventEmitter<any>();

  // Grid State Change Events (for static mode)
  @Output() gridFilterChange = new EventEmitter<any[]>();
  @Output() gridSortChange = new EventEmitter<any[]>();
  @Output() gridGroupChange = new EventEmitter<any[]>();

  constructor() {
    // Subscribe to the reactive data pipeline once
    // switchMap will automatically cancel previous requests when new data is emitted
    this.gridDataResponse$.subscribe(({ res, initialLoad }) => {
      this.handleGridDataResponse(res, initialLoad);
    });
  }

  @HostListener('window:onAddRecord', ['$event']) onAddRecord(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.onAddEvent();
    }
  }

  @HostListener('window:onDownload', ['$event']) onDownload(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.onDownloadData(ReportType.Excel);
    }
  }

  @HostListener('window:onDownloadPdf', ['$event']) onDownloadPdf(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.onDownloadData(ReportType.Pdf);
    }
  }

  @HostListener('window:onDownloadCsv', ['$event']) onDownloadCsv(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.onDownloadData(ReportType.CSV);
    }
  }


  @HostListener('window:onRefresh', ['$event']) onRefresh(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.refreshGrid();
    }
  }

  @HostListener('window:onGlobalFilter', ['$event']) onGlobalFilter(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      delete event?.detail?.currentComponent;
      this._value.parameters = {
        ...this._value.parameters,
        ...event?.detail
      };
      this.refreshGrid();
    }
  }

  @HostListener('window:onSaveState', ['$event']) onSaveState(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.manageState('save');
    }
  }

  @HostListener('window:onRestoreState', ['$event']) onRemoveState(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      this.manageState('remove');
    }
  }

  @HostListener('window:onCustomViewChange', ['$event']) onCustomViewChangeEvent(event: any) {
    if (event?.detail?.currentComponent === this._value.component.key) {
      delete event?.detail?.currentComponent;
      this.onCustomViewChange(event.detail);
    }
  }

  /**
   * Handles grid view button clicks via TabSDK event broadcasting
   * Listens for custom 'onGridViewButtonClick' events
   *
   * Event detail structure:
   * - componentKey: Grid component key to identify which grid triggered the event
   * - rowKey: Primary key value of the clicked row
   * - columnField: Field name where view button is placed
   */
  @HostListener('window:onGridViewButtonClick', ['$event']) onGridViewButtonClick(event: any) {
    // Check if this event is for this grid instance
    if (event?.detail?.instance !== this.gridUniqueId) {
      return; // Not our grid
    }

    const { rowKey } = event.detail;
    if (!rowKey) {
      return;
    }

    const gridData = this.gridData?.result || this.gridData;

    // Find row data by primary key
    const rowData = gridData?.find(
      (row: any) => String(row[this.currentPrimaryKeyField]) === String(rowKey)
    );

    if (!rowData) {
      return;
    }

    // Call existing view option click handler
    this.onViewOptionClick(rowData);
  }

  @HostListener('document:click', ['$event']) onDocumentClick(event: any): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.e-contextmenu')) {
      this.isContextMenuOpen = false;
    }
  }

  /**
   * Event handler for the expand/collapse toggle event.
   * Checks if the current chart instance is within the expanded/collapsed container
   * and handles chart resizing accordingly.
   */
  @HostListener('window:tabExpandToggle', ['$event']) onTabExpandToggle(event: any) {
    const { targetId } = event.detail;
    if (!targetId) return;

    // Find the expandable container that was toggled
    const expandableContainer = document.querySelector(`[data-expandable-id="${targetId}"]`) as HTMLElement;
    if (!expandableContainer) return;


    const isGridInExpandableContainer = expandableContainer.contains(this.gridInstance.element);
    if (isGridInExpandableContainer) {
      const isExpanded = expandableContainer.getAttribute('data-tab-expanded') === 'true';
      this.handleGridResize(isExpanded);
    }
  }

  @Input() public set value(v: any) {
    this.initializeComponent(v);
  }

  public get value(): any {
    if (isEmpty(this._value.component) && isEmpty(this._value.componentValue) && isEmpty(this._value.otherFormdata)) {
      return undefined;
    }
    return this._value;
  }

  ngOnDestroy(): void {
    if (this.currentGridExtension?.cleanup) {
      this.currentGridExtension.cleanup();
    }
    this.lookupCache.clear();
    this.onQuickViewAfterClose();

    this.timeouts.forEach(timeout => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
    this.timeouts = [];
  }

  // UPDATED: Enhanced calculateAvailableSpace method
  calculateAvailableSpace() {
    this.gridHeight = this.helperFunctionService.calculateAvailableSpace(
      FormioCustomComponentSelector.Grid,
      ['.e-gridheader', '.e-toolbar', '.e-pager'],
      12,
      this.gridUniqueId
    );
  }

  private handleGridResize(isExpanded: boolean): void {
    // Store original dimensions on first expand
    if (isExpanded && !this.originalDimensions) {
      this.originalDimensions = {
        height: this.gridInstance.height
      };
    }

    if (isExpanded) {
      this.calculateAvailableSpace();
      this.gridInstance!.height = this.gridHeight;

    } else {
      this.gridInstance!.height = this.originalDimensions?.height;
    }
  }

  async initializeComponent(v: any) {
    const { component, otherFormdata, componentValue, parameters } = v;

    this._value.otherFormdata = otherFormdata ?? this._value.otherFormdata;
    this._value.componentValue = componentValue ?? this._value.componentValue;
    this._value.parameters = parameters ?? this._value.parameters;

    if (component && isEmpty(this._value.component)) {
      this._value.component = component;
      if (!this.isInDesigningMode) {
        this.windowEvents();
        await this.processGridExtension();
      }
      this.executeGridExtensionEvent('afterComponentJsonAssign', this._value.component);
      this.activeCustomViewId = this.helperFunctionService.getDefaultCustomViewIdFromUP(this._value.component, this._value.parameters, ComponentType.Grid);
      this.configureComponentSettings();
      this.configureToolBarItems();
      this.processUrlFilters();
      this.retrieveDataIfNeeded();
      this.configureActions();
      // Add this call at the end of initializeComponent method, but only if favorites are enabled
      if (!this.isInDesigningMode && this._value.component?.Configuration?.componentValue?.enableFavoriteRecords) {
        this.subscribeToFavoriteChanges();
      }
    }
  }

  /**
   * Adds event listeners to the window for the 'onRefresh', 'onDownload', and 'onAddRecord' events.
   * When these events are triggered, the corresponding methods are called.
   * Additionally, a custom event 'onSetLayoutValue' is dispatched with the current component configuration as the detail.
   */
  private windowEvents() {
    // Dispatch custom event 'onSetLayoutValue' with the current component configuration as the detail
    window.dispatchEvent(
      new CustomEvent('onSetLayoutValue', {
        detail: {
          currentComponent: this._value.component.key,
          listLayoutTitle: this._value.component.label
        }
      })
    );
  }

  /**
   * Processes the URL filters.
   * The filters are retrieved from the query string of the current route and are filtered by the current component's data source query ID.
   * The filters are then parsed and used to create the filter settings for the grid.
   * @private
   */
  private processUrlFilters() {
    const { filters = '{}' } = this._activatedRoute?.snapshot?.queryParams || {};
    const filterJson = (JSON.parse(filters) || {})[this._value.component?.Configuration?.componentValue?.dataSourceQueryId] || {};

    this.filterSettings = {
      type: 'Menu',
      columns: (filterJson.columnSearch ?? []).map((item: { fieldName: any; filterType: number; value: any }) => ({
        field: item.fieldName,
        operator: this.getOperatorKeyFromValue(item.filterType),
        value: item.value
      }))
    };
  }

  /**
   * Configures toolbar items based on the component configuration.
   * For each setting, if it is enabled in the component configuration,
   * the corresponding function is called to add the toolbar item.
   */
  async configureToolBarItems() {
    // Destructure the component configuration
    const { componentValue } = this._value.component?.Configuration ?? {};

    // If there is no component configuration, return
    if (!componentValue) return;

    /**
     * An object mapping toolbar setting names to their corresponding functions.
     * Each function adds a toolbar item.
     */
    const toolbarSettings = {
      isEnableExport: { fn: this.configureExportButton }, // Function to add export button
      isAllowAdd: { fn: this.configureAddButton }, // Function to add add button
      isAllowEdit: { fn: this.configureEditButton }, // Function to add edit button
      isAllowDelete: { fn: this.configureDeleteButton }, // Function to add delete button
      isShowColumChooser: { fn: this.addColumnChooser }, // Function to add column chooser
      isshowSearch: { fn: this.addSearch }, // Function to add search
      isEnablePersistence: { fn: this.configureStateButton }, // Function to configure state button
      isShowRefresh: { fn: this.configureSyncButton }, // Function to configure sync refresh button
      isAllowCustomView: { fn: this.configureCustomViewButton }
    };

    // Iterate over each setting
    for (const [setting, { fn }] of Object.entries(toolbarSettings)) {
      // If the setting is enabled in the component configuration, call the corresponding function
      if (componentValue[setting] && fn) {
        fn.call(this);
      }
    }

    // After all configure methods are called, add separator
    const gridActionsCount = this.contextMenuItems.filter((item: any) => item.id?.startsWith('grid-action-')).length;
    const hasUserDefinedActions = this._value.component?.EventActionConfiguration?.componentValue?.userDefinedActionConfig?.length > 0;

    if (gridActionsCount > 0 && hasUserDefinedActions) {
      this.contextMenuItems.push({ separator: true });
    }
  }

  addColumnChooser() {
    this.toolbarItems.push('ColumnChooser');
  }

  addSearch() {
    this.toolbarItems.push('Search');
  }

  configureAddButton() {
    // Static Mode Guard: CRUD operations not supported
    if (this.isStaticMode) return;

    const { componentValue } = this._value.component?.Configuration ?? {};
    const doesHavePermission = this.permissionService.doesHaveDataAccessPermission(
      AccessType.CREATE,
      undefined,
      componentValue?.dataSourceQueryId
    );

    if (!componentValue || !componentValue.isAllowAdd || !doesHavePermission) return;

    this.toolbarItems.push('Add');
    this.editOptions = {
      ...this.editOptions,
      allowAdding: true
    };

    if (componentValue.editingMode === 'rowLevel') {
      if (componentValue.editingTypeForAdd === 'inline') {
        this.editOptions.mode = 'Normal';
      } else if (componentValue.editingTypeForAdd === 'defaultDialog') {
        this.editOptions.mode = 'Dialog';
      }
    } else if (componentValue.editingMode === 'batchLevel') {
      this.editOptions.mode = 'Batch';
    }
  }

  configureEditButton() {
    // Static Mode Guard: CRUD operations not supported
    if (this.isStaticMode) return;

    const { editingMode, editingTypeForEdit } = this._value.component?.Configuration.componentValue ?? {};
    const doesHavePermission = this.permissionService.doesHaveDataAccessPermission(
      AccessType.UPDATE,
      undefined,
      this._value.component?.Configuration?.componentValue?.dataSourceQueryId
    );

    if (!editingMode || editingMode !== 'rowLevel' || !doesHavePermission) return;

    this.editOptions.allowEditing = true;
    if (editingMode === 'rowLevel') {
      this.contextMenuItems.push({
        text: TabSdk.translator.translate('Edit'),
        iconCss: 'fa fa-edit',
        id: 'grid-action-edit'
      });

      if (editingTypeForEdit === 'inline') {
        this.editOptions.mode = 'Normal';
      } else if (editingTypeForEdit === 'defaultDialog') {
        this.editOptions.mode = 'Dialog';
      }
    } else if (editingMode === 'batchLevel' && editingTypeForEdit) {
      this.editOptions.mode = 'Batch';
      this.toolbarItems.push(...['Edit', 'Update', 'Cancel']);
    }
  }

  configureDeleteButton() {
    // Static Mode Guard: CRUD operations not supported
    if (this.isStaticMode) return;

    const { editingMode } = this._value.component?.Configuration.componentValue ?? {};
    const doesHavePermission = this.permissionService.doesHaveDataAccessPermission(
      AccessType.DELETE,
      undefined,
      this._value.component?.Configuration?.componentValue?.dataSourceQueryId
    );

    if (!editingMode || editingMode !== 'rowLevel' || !doesHavePermission) return;

    this.editOptions.allowDeleting = true;
    if (editingMode === 'rowLevel') {
      this.contextMenuItems.push({
        text: TabSdk.translator.translate('Delete'),
        iconCss: 'e-icons e-trash',
        id: 'grid-action-delete'
      });
    } else if (editingMode === 'batchLevel') {
      this.editOptions.mode = 'Batch';
      this.toolbarItems.push('Delete');
    }
  }

  configureViewButton(columnData: any) {
    const { componentValue } = this._value.component?.Configuration ?? {};
    if (!columnData?.length || !componentValue.isAllowView) return;

    const doesHavePermission = this.permissionService.doesHaveDataAccessPermission(
      AccessType.READ,
      undefined,
      componentValue?.dataSourceQueryId
    );
    if (!doesHavePermission) return;

    // Find the column and apply template
    const targetColumn = columnData.find((col: any) => col.field === componentValue?.viewOptionColumnField);
    if (targetColumn) {
      // Store the column field
      const viewOptionColumnField = targetColumn.field;
      const existingTemplate = targetColumn.template;

      if (existingTemplate?.length) {
        // User has custom string template - merge it with view button
        targetColumn.template = this.createMergedViewTemplate(existingTemplate);
      } else {
        // No custom template - use default template with view button
        targetColumn.template = this.getDefaultViewTemplate(viewOptionColumnField);
      }
    }
  }

  /**
   * Creates merged template combining custom HTML template + view button
   * Uses TabSDK event broadcasting for click handling
   *
   * @param customTemplate - User's custom string template
   * @returns Combined template string
   */
  private createMergedViewTemplate(customTemplate: string): string {
    return `
      <div class="combined-cell-content">
        ${customTemplate}
        ${this.getViewButtonHTML()}
      </div>
    `;
  }

  /**
   * Returns default template when no custom template exists
   * Shows field value + view button
   *
   * @returns Default template string
   */
  private getDefaultViewTemplate(viewOptionColumnField: string): string {
    return `
      <div class="combined-cell-content">
        \${${viewOptionColumnField}}
        ${this.getViewButtonHTML()}
      </div>
    `;
  }

  /**
   * Generates view button HTML with TabSDK event broadcasting
   * Uses inline onclick to broadcast custom event with row context
   *
   * @returns View button HTML string
   */
  private getViewButtonHTML(): string {
    const primaryKeyField = this.currentPrimaryKeyField;

    return `
      <div class="grid-view-option"
           onclick="TabSdk.util.broadcastEvent(\`onGridViewButtonClick\`, {
             instance: \`${this.gridUniqueId}\`,
             rowKey: \`\${${primaryKeyField}}\`
           });">
        <i class="fa fa-retweet"></i>View
      </div>
    `;
  }

  configureStateButton() {
    this.toolbarItems.push(
      { text: 'Save', tooltipText: 'Save', id: 'customSaveState', align: 'left' },
      { text: 'Restore', tooltipText: 'Restore', id: 'customRemoveState', align: 'left' }
    );
  }

  configureSyncButton() {
    this.toolbarItems.push({ text: 'Refresh', tooltipText: 'Refresh', id: 'customRefreshGrid', align: 'left' });
  }

  configureCustomViewButton() {
    // Static Mode Guard: Custom views not supported
    if (this.isStaticMode) return;

    this.toolbarItems.push({
      template: this.customViewTemplate,
      align: 'Left'
    });
  }

  configureComponentSettings() {
    const pageCount = this._value.component?.Configuration?.componentValue?.pageCount;
    const pageSize = this._value.component?.Configuration?.componentValue?.pageSize;
    const pageSizes = this._value.component?.Configuration?.componentValue?.pageSizes;

    // In design mode, use pagination limit of 1 to avoid loading full data
    const designModePageSize = this.isInDesigningMode ? 1 : (pageSize && pageSize !== '' ? pageSize : 25);

    this.pageSettings = {
      currentPage: 1,
      pageSize: designModePageSize,
      pageCount: pageCount && pageCount !== '' ? pageCount : 3,
      pageSizes:
        pageSizes && pageSizes !== ''
          ? [...Array(4).keys()].map(i => (pageSizes * (i + 1)).toString()).concat('All')
          : ['25', '50', '100', '500', 'All']
    };

    if (this._value.component?.Configuration?.componentValue?.isAllowFilter) {
      this.columnMenuItems.push('Filter');
    }

    if (this._value.component?.Configuration?.componentValue?.allowNestedGridSupport) {
      this.treeColumnIndex = this._value.component?.Configuration?.componentValue?.treeColumnIndex;
    }
  }

  private retrieveDataIfNeeded() {
    // Static Data Mode: Initialize with static data instead of DSQ
    if (this.isStaticMode) {
      this.initializeStaticMode();
      return;
    }

    // DSQ Mode: Continue with existing logic
    const dataSourceQueryId = this._value.component?.Configuration?.componentValue?.dataSourceQueryId;
    if (dataSourceQueryId) {
      const dsqDetails = TabSdk.metadataUtils.getDataSourceQueryByIdOrName(dataSourceQueryId);
      this.currentAppObject = this.helperFunctionService.getCRUDAppObject(undefined, dataSourceQueryId);
      this.currentPrimaryKeyField = getPrimaryKeyFields(this.currentAppObject)?.[0]?.FieldName ?? '';
      this.initializeDsqColumns(dsqDetails);
    }
  }

  async initializeDsqColumns(dsqDetail: any) {
    const fields = dsqDetail?.fields();
    if (fields?.length) {
      let columnData = this.gridColumnHelper.convertToSyncfusionColumnFormat(fields);
      if (!this.isInDesigningMode) {
        columnData = this.loadState(columnData);
        await this.processLookupColumns(columnData);
        this.loadFieldConfiguration(columnData);
        this.configureViewButton(columnData);
      }
      this.executeGridExtensionEvent('beforeGridColumnAssign', columnData);
      // Assign translated columns
      this.gridColumns = this.translateHeaders(columnData);
      this.addActionColumnIfNecessary();
      // this.setSearchStringForActiveRowFromUrl();
      this.fetchAPIData(true);
    } else {
      console.error('Dsq details not found for provided DSQ ID :', dsqDetail);
      this.calculateAvailableSpace();
    }

    if (this.isInDesigningMode) {
      const timeout = setTimeout(() => this.isShowGrid = true, 200);
      this.timeouts.push(timeout);
    } else {
      this.isShowGrid = true;
    }
  }

  /**
   * Translates header text of grid columns
   * @param columnData - Grid columns
   * @returns Translated grid columns
   */
  translateHeaders(columnData: any) {
    return columnData.map((column: any) => {
      return {
        ...column,
        headerText: TabSdk.translator.translate(column?.headerText)
      };
    });
  }

  loadState(columnData: any) {
    let data: any = this.helperFunctionService.getUserPersonalization(
      this._value.component?.screen,
      this._value.component?.key,
      ComponentType.Grid,
      this.activeCustomViewId || this._value.component?.Configuration?.componentValue?.dataSourceQueryId
    );

    if (!data?.length && this.activeCustomViewId?.length) {
      data = this.helperFunctionService.getUserPersonalization(
        this._value.component?.screen,
        this._value.component?.key,
        ComponentType.Grid,
        this._value.component?.Configuration?.componentValue?.dataSourceQueryId
      );
    }
    this.executeGridExtensionEvent('loadCustomPersonalization', data);

    if (data && data.length > 0) {
      this.userPersonalizeData = data[0];
      if (this.userPersonalizeData?.Config) {
        return this.loadStateConfiguration(JSON.parse(this.userPersonalizeData.Config), columnData);
      }
    } else if (this.userPersonalizeData) {
      delete this.userPersonalizeData;
    }

    return columnData;
  }

  loadStateConfiguration(state: any, columnData: any) {
    if (state?.sortSettings?.columns?.length !== 0) {
      this.sortSettings = state.sortSettings;
    }

    if (state?.searchSettings?.key !== '') {
      this.searchSettings = state.searchSettings;
    }

    if (state?.filterSettings?.columns?.length !== 0) {
      this.filterSettings = {
        ...this.filterSettings,
        ...state.filterSettings
      };
    }

    if (state?.pageSettings) {
      this.pageSettings = {
        ...this.pageSettings,
        ...state.pageSettings
      };
    }

    return this.loadColumnState(state, columnData);
  }

  // load column state
  private loadColumnState(state: any, columnData: any) {
    if (!state?.columns?.length) columnData;

    const updatedColumnData: any = [];

    // extract saved column from state
    state?.columns?.forEach((stateCol: any) => {
      const columns = columnData.find((element: any) => element.field === stateCol.field);
      if (columns) {
        const removeUnnecessarySettingsFromColState = ['edit', 'validationRules', 'allowEditing'];
        removeUnnecessarySettingsFromColState.forEach((setting: string) => {
          delete stateCol[setting];
        });
        updatedColumnData.push({ ...columns, ...stateCol });
      }
    });

    // extract remaining columns like Actions
    columnData.forEach((element: any) => {
      if (!updatedColumnData.find((col: any) => col.field === element.field)) {
        if (element.field !== 'Action') {
          element.visible = false;
          updatedColumnData.push(element);
        } else {
          updatedColumnData.unshift(element);
        }
      }
    });

    return updatedColumnData;
  }

  async processLookupColumns(columnData: any) {
    const lookupColumns = columnData.filter((item: any) => 'LookUpDetails' in item);
    for (const column of lookupColumns) {
      try {
        await this.loadLookupDetails(column);
      } catch (error) {
        console.error(`Error handling lookup details for field ${column.dataField}:`, error);
      }
    }
  }

  async loadLookupDetails(gridColumn: any) {
    let response: any;
    if (gridColumn?.LookUpDetails?.selectQuery) {
      response = await TabSdk.crud.executeSelectExecutor(gridColumn.LookUpDetails.selectQuery, undefined, this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode));
    } else {
      const executeRAWDSQWithNameData = {
        AppObjectName: gridColumn?.LookUpDetails?.LookupObject,
        DSQName: 'list_' + gridColumn?.LookUpDetails?.LookupObject,
        IsServerSidePagination: false
      };
      response = await firstValueFrom(this.tabGetService.executeRAWDSQWithName(executeRAWDSQWithNameData, this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode)));
    }

    const lookupData = response?.Result ?? [];
    if (this._value.component?.Configuration?.componentValue?.allowNestedGridSupport) {

      this.createLookupCache(gridColumn, lookupData);

      gridColumn.edit = {
        params: {
          dataSource: new DataManager(lookupData),
          query: new Query(),
          fields: {
            text: gridColumn.LookUpDetails.DisplayField,
            value: gridColumn.LookUpDetails.LookupField
          },
          change: (args: any) => {
            if (args.isInteracted) {
              this.gridInstance?.endEdit();
              this.gridInstance?.refresh();
            }
          }
        }
      };

      gridColumn.foreignKeyValue = null;

    } else {
      gridColumn.dataSource = lookupData;
    }
  }

  /**
   * Caches lookup data for a grid column.
   * 
   * @param gridColumn - The grid column containing lookup details.
   * @param lookupData - The data to be cached, containing value and display fields.
   */
  private createLookupCache(gridColumn: any, lookupData: any): void {
    // Initialize a map to store value-display pairs
    const lookupMap = new Map<any, string>();
    const valueField = gridColumn.LookUpDetails.LookupField;
    const displayField = gridColumn.LookUpDetails.DisplayField;

    // Populate the map with lookup data
    for (let i = 0; i < lookupData.length; i++) {
      lookupMap.set(lookupData[i][valueField], lookupData[i][displayField]);
    }

    // Store the map in the lookup cache using the column field as the key
    this.lookupCache.set(gridColumn.field, lookupMap);
  }

  private loadFieldConfiguration(columnData: any) {
    const fieldConfiguration = this._value?.component?.Configuration?.componentValue?.fieldConfiguration?.componentValue;
    if (fieldConfiguration) {
      for (let gridColumn of columnData) {
        if (fieldConfiguration[gridColumn.field]) {
          this.getFieldConfiguration(gridColumn, fieldConfiguration[gridColumn.field]);
        }
      }
    }
  }

  private getFieldConfiguration(gridColumn: any, columnConfiguration: any) {
    if (columnConfiguration?.columnHeader?.length) {
      gridColumn['headerText'] = columnConfiguration?.columnHeader;
    }

    if (columnConfiguration?.selectTemplate) {
      if (columnConfiguration.selectTemplate === 'HTML') {
        gridColumn['template'] = columnConfiguration?.htmlEditor;
      } else if (columnConfiguration.selectTemplate === 'BUTTON') {
        gridColumn['template'] = '<button class="btn btn-primary">Button</button>';
      } else if (columnConfiguration.selectTemplate === 'IMAGE') {
        gridColumn['template'] = `<img src="${columnConfiguration?.imageUrl}">`;
      }
    }

    if (columnConfiguration?.validationRules?.required) {
      gridColumn['validationRules'] = {
        required: true,
        noOnlySpaces: [(args: { value: string }) => {
          return args.value.trim().length > 0;
        }, 'Field cannot contain only whitespace.']
      };
    }

    if (columnConfiguration?.disableEditing) {
      gridColumn['allowEditing'] = false;
    } else {
      let doesHavePermission = true;
      if (columnConfiguration?.permissionsScreen?.componentValue?.permissionType) {
        switch (PermissionType[columnConfiguration?.permissionsScreen?.componentValue?.permissionType] as any) {
          case 1:
            doesHavePermission = this.permissionService?.doesHaveConfiguredPermission(
              columnConfiguration?.permissionsScreen?.componentValue?.editablePermission
            );
            break;

          case 2:
            doesHavePermission = this.permissionService?.doesHaveDataAccessPermission(
              columnConfiguration?.permissionScreen?.componentValue?.accessType,
              columnConfiguration?.permissionScreen?.componentValue?.appObjectId
            );
            break;
        }
      }

      if (!doesHavePermission) {
        gridColumn['allowEditing'] = false;
      } else if (
        columnConfiguration?.destinationField !== '' &&
        (columnConfiguration?.editColumConfigMode === 'lookupField' ||
          columnConfiguration?.editColumConfigMode === 'url')
      ) {
        this.processEditFieldLookupConfig(gridColumn, columnConfiguration);
      }
    }
  }

  private processEditFieldLookupConfig(gridColumn: any, columnConfiguration: any) {
    let dropdown: any;
    let dropdownElem: any;
    let dropdownValue: any;

    gridColumn['edit'] = {
      create: () => {
        dropdownElem = document.createElement('input');
        return dropdownElem;
      },
      destroy: () => {
        dropdown.destroy();
      },
      read: () => {
        return dropdownValue;
      },
      write: async (rowArgs: any) => {
        dropdownValue = rowArgs.rowData[rowArgs.column.field];
        const config = columnConfiguration?.configScreen?.componentValue;

        dropdown = new DropDownList({
          ignoreAccent: true,
          allowFiltering: true,
          filterBarPlaceholder: 'Type to search',
          value: rowArgs.rowData[columnConfiguration.destinationField],
          fields: { text: config?.displayField, value: config?.valueField },
          dataSource: [],
          change: (dropdownArgs: any) => this.onEditFieldDropdownChange(rowArgs, dropdownArgs, columnConfiguration)
        });

        dropdown.appendTo(dropdownElem);

        if (columnConfiguration?.editColumConfigMode === 'lookupField') {
          const removeUnnecessaryData = [
            'taskData',
            'parentItem',
            'checkboxState',
            'index',
            'level',
            'parentUniqueID',
            'uniqueID'
          ];
          const params = JSON.parse(JSON.stringify(rowArgs.rowData));
          removeUnnecessaryData.forEach((key: any) => delete params[key]);

          this.helperFunctionService.getDSQData(config?.dataSourceQueryId, params, undefined, false, this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode))
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((res: any) => {
              dropdown.dataSource = res?.Result ?? [];
              dropdown.value =
                dropdown?.dataSource?.find((item: any) => item[config?.displayField] === dropdownValue)?.[
                config?.valueField
                ] ?? dropdownValue;
            });
        } else if (columnConfiguration?.editColumConfigMode === 'url') {
          this.apiService.getByUrl(config?.url, { headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode) })
            .subscribe((res: any) => {
              dropdown.dataSource = res?.Result ?? [];
              dropdown.value =
                dropdown?.dataSource?.find((item: any) => item[config?.displayField] === dropdownValue)?.[
                config?.valueField
                ] ?? dropdownValue;
            });
        }
      }
    };
  }

  private async onEditFieldDropdownChange(rowArgs: any, dropdownArgs: any, config: any) {
    if (dropdownArgs.isInteracted) {
      if (this.currentAppObject?.ID && this.currentPrimaryKeyField) {
        const res = await TabSdk.crud.update({
          appObjectIdOrName: this.currentAppObject.ID,
          data: { [config.destinationField]: dropdownArgs.value },
          criteria: { [this.currentPrimaryKeyField]: rowArgs.rowData[this.currentPrimaryKeyField] }
        }, { skipDefaultToast: true, headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode) });

        this.gridInstance.endEdit();

        if (this._value?.component?.Configuration?.componentValue?.allowNestedGridSupport) {
          const data = {
            ...rowArgs?.rowData,
            [config.destinationField]: dropdownArgs.value,
            [rowArgs.column.field]: dropdownArgs.itemData[config?.configScreen?.componentValue?.displayField]
          };
          const currentIndex = this.gridInstance?.grid?.getRowIndexByPrimaryKey(rowArgs?.rowData?.[this.currentPrimaryKeyField]);
          this.gridInstance?.grid?.updateRow(currentIndex, data);
        }

        if (res.StatusCode == '200') {
          TabSdk.ui.showSuccess({ message: TranslationToken.Updated });
        } else {
          TabSdk.ui.showError({ message: res.Errors[0] });
        }
      }
    }
  }


  addActionColumnIfNecessary(): void {
    const hasContextMenuItems = this.contextMenuItems.length > 0;
    const hasFavoritesFunctionality = this._value.component?.Configuration?.componentValue?.enableFavoriteRecords;

    // If any of the conditions are met, add the action column
    if (hasContextMenuItems || hasFavoritesFunctionality) {
      this.treeColumnIndex += 1;
      let needsEditTemplate = !this._value.component?.Configuration?.componentValue?.allowNestedGridSupport;
      needsEditTemplate = needsEditTemplate && this._value.component?.Configuration?.componentValue?.editingTypeForEdit === 'inline';

      const actionColumn: any = {
        field: 'Action',
        headerText: '',
        width: 45,
        textAlign: 'center',
        template: this.actionTemplate,
        showColumnMenu: false,
        showInColumnChooser: false,
        allowSorting: false,
        allowFiltering: false,
        allowEditing: false,
        allowReordering: false
      };

      if (needsEditTemplate) {
        actionColumn.allowEditing = true;
        actionColumn.edit = {
          create: () => this.createEditButtons(),
          read: () => '',
          write: () => { }
        };
      }

      this.gridColumns.unshift(actionColumn);
    }
  }

  private createEditButtons() {
    const wrapper = document.createElement('div');

    // Button configurations
    const buttonConfigs = [
      { action: 'save', title: 'Save', icon: 'e-save' },
      { action: 'cancel', title: 'Cancel', icon: 'e-cancel-icon' }
    ];

    // Create buttons using loop
    buttonConfigs.forEach(config => {
      const btn = document.createElement('button');
      btn.className = 'e-btn e-flat e-small me-1';
      btn.title = config.title;
      btn.innerHTML = `<span class="e-icons ${config.icon}"></span>`;
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (config.action === 'save') {
          this.gridInstance?.endEdit();
        } else if (config.action === 'cancel') {
          this.gridInstance?.closeEdit();
        }
      });

      wrapper.appendChild(btn);
    });

    return wrapper;
  }

  private getDSQParams() {
    const payload: any = {};

    if (
      this._value?.component?.Configuration?.componentValue?.allowNestedGridSupport ||
      this._value?.component?.Configuration?.componentValue?.isClientSidePagination
    ) {
      payload['IsServerSidePagination'] = false;
    } else {
      payload['GlobalSearch'] = this.searchSettings?.key ?? '';
      payload['sort'] = this.getSortPayload();

      if (this._value?.component?.Configuration?.componentValue?.isPageable) {
        payload['IsServerSidePagination'] = true;
        payload['Pager'] = {
          PageNo: this.pageSettings?.currentPage,
          NoOfRecords: this.pageSettings?.pageSize
        };
      }
    }

    if (this.isInDesigningMode) {
      payload['IsServerSidePagination'] = true;
      payload['Pager'] = {
        PageNo: 1,
        NoOfRecords: 1
      };
    }

    if (this.activeCustomViewId) {
      payload.CustomViewID = this.activeCustomViewId;
    }

    return payload;
  }

  private getSortPayload() {
    if (!this.sortSettings?.columns) return [];

    return this.sortSettings.columns.map((sortedCol: any) => {
      return {
        fieldID: sortedCol?.field,
        sortSequence: sortedCol?.direction.toLowerCase() === 'ascending' ? 1 : 2
      };
    });
  }

  private getFilterPayload(filterSettings: any = []) {
    if (!filterSettings?.columns?.length) return [];
    return (filterSettings as any).columns.map((filteredCol: any) => {
      return {
        fieldName: filteredCol?.field,
        filterType: operatorMapping[filteredCol?.operator.toLowerCase()],
        value: filteredCol?.value.toString()
      };
    });
  }
  private getOperatorKeyFromValue(value: number) {
    for (const [key, val] of Object.entries(operatorMapping)) {
      if (val === value) {
        return key;
      }
    }
    return null;
  }

  fetchAPIData(initialLoad: boolean = false): void {
    // Prepare reqtokens with column search filters
    const reqtokens = this._value?.parameters;
    const columnSearchFilters = this.getFilterPayload(this.filterSettings);
    if (columnSearchFilters.length) {
      reqtokens.columnSearch = columnSearchFilters;
    }

    // Trigger data fetch - switchMap will automatically cancel previous request
    this.fetchTrigger$.next({
      initialLoad: initialLoad,
      dsqId: this._value.component?.Configuration?.componentValue?.dataSourceQueryId,
      reqtokens: reqtokens,
      params: this.getDSQParams(),
      headers: TabSdk.util.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode)
    });
  }

  /**
   * Handles grid data response from the reactive pipeline
   * Extracted from fetchAPIData for better separation of concerns
   */
  private handleGridDataResponse(response: any, isInitialLoad: boolean): void {
    if (response && response?.Result) {
      if (this._value.component?.Configuration?.componentValue?.enableFavoriteRecords) {
        response?.Result?.forEach((item: any) => {
          item._isFavorite = this.isFavorite(item);
        });
      }

      if (
        !this._value.component?.Configuration?.componentValue?.allowNestedGridSupport &&
        !this._value?.component?.Configuration?.componentValue?.isClientSidePagination
      ) {
        this.gridData = {
          result: response?.Result,
          count: response?.Result?.length > 0 ? response?.TotalRecords : response?.RecordAffectted
        };
      } else if (this._value.component?.Configuration?.componentValue?.allowNestedGridSupport) {
        this.gridData = this.transformTreeGridData(response.Result);
        this.expandActiveRow();
      } else {
        this.gridData = response.Result;
      }

      this.exceuteFormioCustomEvent('GridDataUpdated', this.gridData);
      this.executeGridExtensionEvent('gridDataUpdated', this.gridData);

      // Push row data context for template editor autocomplete
      if (this.isInDesigningMode) {
        this.pushGridTemplateContext(response.Result);
      }

      if (isInitialLoad) {
        this.calculateAvailableSpace();
      }
    }
  }

  /**
   * Push row data context for grid column template editor autocomplete
   * Uses first row as a sample to provide field names and value types
   * Falls back to column metadata if no data rows
   */
  private pushGridTemplateContext(rows: any[]): void {
    const firstRow = rows?.[0];
    // Syncfusion grid string templates also expose `index` (row index)
    const syncfusionDefaults = { index: 0 };

    if (firstRow && typeof firstRow === 'object') {
      this.monacoContextService.setTemplateContext(TemplateContextKey.GridColumn, { ...firstRow, ...syncfusionDefaults });
    } else if (this.gridColumns?.length) {
      // No data - build skeleton from column field names
      const skeleton: Record<string, any> = {};
      for (const col of this.gridColumns) {
        if (col.field) skeleton[col.field] = '';
      }
      this.monacoContextService.setTemplateContext(TemplateContextKey.GridColumn, { ...skeleton, ...syncfusionDefaults });
    }
  }

  private transformTreeGridData(data: any) {
    const existingParentIDs = new Set(data.map((item: any) => item.ID));
    data.forEach((item: any) => {
      if (item.ParentID !== undefined && !existingParentIDs.has(item.ParentID)) {
        item._parentID = item.ParentID;
        item.ParentID = null;
      }
    });
    return data;
  }

  private expandActiveRow() {
    if (this.activeRow) {
      const timeout = setTimeout(() => {
        const primatyKey = this.activeRow[this.currentPrimaryKeyField];
        this.gridInstance?.expandByKey(primatyKey);
      }, 100);
      this.timeouts.push(timeout);
    }
  }

  // Triggered by html for custom views start
  onCustomViewChange(event: any) {
    if (event?.Id && event?.Id !== this.activeCustomViewId) {
      this.activeCustomViewId = event?.Id;
      delete this.userPersonalizeData;
      this.gridColumns = this.loadState(this.gridColumns);
      this.fetchAPIData();
    }
  }

  private async deleteData(args: any) {
    if (this.currentAppObject && this.currentPrimaryKeyField) {
      const isConfirmed = await TabSdk.ui.confirmDelete();

      if (isConfirmed) {
        const res = await TabSdk.crud.delete(
          {
            appObjectIdOrName: this.currentAppObject?.ID,
            criteria: { [this.currentPrimaryKeyField]: args.rowData[this.currentPrimaryKeyField] }
          },
          {
            skipDefaultToast: true,
            headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component)
          }
        );

        if (!res.Errors?.length) {
          this.refreshGrid();
          TabSdk.ui.showSuccess({ message: TranslationToken.Deleted });
        } else {
          TabSdk.ui.showError({ message: TranslationToken.RecordDeleteFailed });
        }
      }
    }
  }

  toolbarClick(args: any) {
    if (args.item.properties.text === 'Add') {
      if (this._value?.component?.Configuration?.componentValue?.editingTypeForAdd == 'customScreen') {
        args.cancel = true;
      }
      this.onAddEvent();
    }
    if (args.item.id.includes('customSaveState')) {
      this.manageState('save');
    }
    if (args.item.id.includes('customRemoveState')) {
      this.manageState('remove');
    }
    if (args.item.id.includes('customRefreshGrid')) {
      this.refreshGrid();
    }
  }

  private onAddEvent() {
    if (this._value?.component?.Configuration?.componentValue?.editingTypeForAdd == 'customScreen') {
      const addContainer = this._value.component?.Configuration?.componentValue?.addContainer;
      const screenId = addContainer?.addScreen;
      const submission = TabSdk.util.getStoredComponentContext(
        this._value.component?.Configuration?.componentValue?.dataSourceQueryId,
        this._value.component.key
      );
      const mode = addContainer?.screenOpeningModeForAddScreen;
      const configurations = this.parseScreenConfigurations(addContainer?.addScreenConfigurations?.componentValue);
      this.executeCommandCustomScreenAction(screenId, mode, null, submission, 'Add', configurations);
    }
  }

  public manageState(mode: string): void {
    // Static Mode Guard: Personalization not supported
    if (this.isStaticMode) {
      this.showStaticModeWarning('Grid personalization/state management');
      return;
    }

    const currentState = this.getCurrentState();
    let userPersonalizeData: any;

    if (!this.userPersonalizeData) {
      userPersonalizeData = {
        ScreenId: this._value.component?.screen,
        ComponentId: this._value.component?.key,
        Type: ComponentType.Grid,
        Key: this.activeCustomViewId || this._value.component?.Configuration?.componentValue?.dataSourceQueryId,
        Config: currentState
      }
    } else {
      userPersonalizeData = {
        ...this.userPersonalizeData,
        Config: currentState
      };
    }

    this.helperFunctionService.manageUserPersonalization(
      mode,
      userPersonalizeData,
      this.activeCustomViewId,
      this._value.component?.Configuration?.componentValue?.dataSourceQueryId,
      this.helperFunctionService.buildComponentRequestHeaders(this._value.component)
    )?.subscribe((res: any) => {
      this.gridColumns = this.loadState(this.gridColumns);
    });
  }

  getCurrentState() {
    const currentState = JSON.parse((this.gridInstance as GridComponent).getPersistData());
    return currentState ? JSON.stringify(this.removeUnnecessarySettingsFromState(currentState)) : null;
  }

  removeUnnecessarySettingsFromState(state: any) {
    state.pageSettings = this.removePageSettingsFromState(state.pageSettings);
    state.searchSettings = this.removeSearchSettingsFromState(state.searchSettings);
    state.sortSettings = this.removeSortSettingsFromState(state.sortSettings);
    state.filterSettings = this.removeFilterSettingsFromState(state.filterSettings);
    state.columns.forEach((column: any, index: number) => {
      if (column.field === 'Action') {
        state.columns.splice(index, 1);
      } else {
        column = this.removeColumnSettingsFromState(column);
      }
    });
    return state;
  }

  removePageSettingsFromState(pageSettings: any) {
    const keysToRemove = ['enableQueryString', 'pageCount', 'pageSize', 'pageSizes', 'totalRecordsCount'];
    keysToRemove.forEach(key => delete pageSettings[key]);
    return pageSettings;
  }

  removeSearchSettingsFromState(searchSettings: any) {
    const keysToRemove = ['fields', 'ignoreAccent', 'ignoreCase', 'operator'];
    keysToRemove.forEach(key => delete searchSettings[key]);
    return searchSettings;
  }

  removeSortSettingsFromState(sortSettings: any) {
    const keysToRemove = ['allowUnsort'];
    keysToRemove.forEach(key => delete sortSettings[key]);
    return sortSettings;
  }

  removeFilterSettingsFromState(filterSettings: any) {
    const keysToRemove = ['type', 'mode', 'ignoreAccent', 'enableCaseSensitivity'];
    keysToRemove.forEach(key => delete filterSettings[key]);
    filterSettings.columns = filterSettings?.columns.map((column: any) => {
      return {
        field: column?.field,
        value: column?.value,
        operator: column?.operator
      };
    });
    return filterSettings;
  }

  removeColumnSettingsFromState(column: any) {
    const keysToRemove = [
      'uid',
      'type',
      'editType',
      'foreignKeyField',
      'foreignKeyValue',
      'columnData',
      'commands',
      'template',
      'LookUpDetails',
      'edit',
      'validationRules',
      'allowEditing'
    ];
    keysToRemove.forEach(key => delete column[key]);
    return column;
  }

  created(event: any) {
    this.executeGridExtensionEvent('onCreate', null);
    if (this._value.component?.Configuration?.componentValue?.allowNestedGridSupport) {
      this.gridInstance.grid.commandClick = this.commandClick.bind(this);
      this.gridInstance.grid.localeObj.currentLocale.EmptyRecord = `
         <div class="no-record-found">
          <span></span>
          <div>There is no data available to display at the moment.</div>
        </div>
      `;
    }

    if (this._value.component?.Configuration?.componentValue?.enableQuickView) {
      this.createTooltipInstance();
    }
  }


  commandClick(args: any) {
    // Static Mode Guard: Block Edit and Delete operations
    if (this.isStaticMode && (args?.commandColumn?.type === 'Edit' || args?.commandColumn?.type === 'Delete')) {
      this.showStaticModeWarning('CRUD operations');
      return;
    }

    let reqtokens: any = { [this.currentPrimaryKeyField]: args?.rowData?.[this.currentPrimaryKeyField] };


    if (args?.commandColumn?.type === 'Edit') {
      const editingType = this._value?.component?.Configuration?.componentValue?.editingTypeForEdit;

      if (editingType == 'customScreen') {
        args.cancel = true;
        const editContainer = this._value.component?.Configuration?.componentValue?.editContainer;
        const screenId = editContainer?.editScreen;
        const mode = editContainer?.screenOpeningModeForEditScreen;
        const configurations = this.parseScreenConfigurations(editContainer?.editScreenConfigurations?.componentValue);
        this.executeCommandCustomScreenAction(screenId, mode, reqtokens, null, undefined, configurations);
      } else if (editingType == 'inline' || editingType == 'defaultDialog') {
        this.processDefaultEdit(args.rowData);
      }

    } else if (args?.commandColumn?.type === 'View') {
      if (this._value?.component?.Configuration?.componentValue?.editingTypeForView == 'customScreen') {
        args.cancel = true;
        const viewContainer = this._value.component?.Configuration?.componentValue?.viewContainer;
        const screenId = viewContainer?.viewScreen;
        const mode = viewContainer?.screenOpeningModeForViewScreen;
        const configurations = this.parseScreenConfigurations(viewContainer?.viewScreenConfigurations?.componentValue);
        this.executeCommandCustomScreenAction(screenId, mode, reqtokens, null, undefined, configurations);
      }
    } else if (args?.commandColumn?.type === 'Delete') {
      args.cancel = true;
      this.deleteData(args);
    }
  }

  private processDefaultEdit(rowData: any) {
    try {
      const grid = this.gridInstance?.grid || this.gridInstance;
      const index = grid.getRowIndexByPrimaryKey(rowData[this.currentPrimaryKeyField]);
      if (index !== -1) {
        grid.selectRow(index);
        grid.startEdit(rowData);
      }
    } catch (error) {
      console.error('Could not select row for edit:', error);
    }
  }

  private parseScreenConfigurations(configValue: any): Record<string, any> | null {
    if (!configValue) return null;
    const parsed = typeof configValue === 'string' ? TabSdk.util.safeJsonParse(configValue) : configValue;
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  private executeCommandCustomScreenAction(screenId: any, mode: any, reqtokens: any, submission: any = null, action: string = 'Modify', configurations?: Record<string, any> | null) {
    const title = action + ' ' + (this.currentAppObject?.DisplayName ?? '');
    const baseConfig = { screenId, reqtokens, submission, refresher: this.refreshGrid, title };
    // User-configured values (title, width, customClass, etc.) override defaults
    const config = configurations ? { ...baseConfig, ...configurations } : baseConfig;

    switch (mode) {
      case 'sidebar':
        TabSdk.ui.showSidebar(config);
        break;

      case 'popupModal':
        TabSdk.ui.showDialog(config);
        break;

      case 'navigation':
        TabSdk.ui.navigateTo({ screenId, reqtokens });
        break;

      case 'newTab':
        TabSdk.ui.openInNewTab({ screenId, reqtokens });
        break;
    }
  }


  dataStateChange(state: any) {
    if (
      this._value.component?.Configuration?.componentValue?.allowNestedGridSupport ||
      this._value?.component?.Configuration?.componentValue?.isClientSidePagination
    ) {
      return;
    }

    switch (state?.action?.requestType) {
      case 'refresh':
        this.refreshGridData();
        break;
      case 'paging':
        this.onPaging(state?.action);
        break;
      case 'searching':
        this.onSearching(state?.action?.searchString);
        break;
      case 'sorting':
        this.onSorting(state?.sorted);
        break;
      case 'filtering':
        this.handleFiltering(state.action);
        break;
    }
  }

  private refreshGridData() {
    this.gridData = { ...this.gridData };
  }

  // pagination
  private onPaging(action: any) {
    this.pageSettings.pageSize = action.pageSize;
    this.pageSettings.currentPage = action.currentPage;
    this.fetchAPIData();
  }

  private onSearching(searchString: string) {
    this.pageSettings.currentPage = 1;
    this.searchSettings.key = searchString?.trim() || '';
    this.fetchAPIData();
  }

  private onSorting(sorted: any[]) {
    this.sortSettings.columns = sorted?.length
      ? sorted.map(column => ({
        field: column?.name,
        direction: column?.direction
      }))
      : [];
    this.fetchAPIData();
  }

  private handleFiltering(action: any) {
    if (action.action === 'clearFilter') {
      this.onClearFilter(action.currentFilterColumn?.field);
    } else if (action.action === 'filter') {
      this.onFilter(action.currentFilterObject);
    }
  }

  private handleFilterAPICall(filterSettings: any[]) {
    // Direct API call approach - pass filters directly via reqtokens
    // Reset to page 1 when filters are applied
    this.pageSettings.currentPage = 1;
    this.fetchAPIData();

    // ===== URL-based filter approach (commented for future use) =====
    // Instead of calling fetchAPIData() here, we will update it in URL Query Param and refresh
    // const dsqId = this._value?.component?.Configuration?.componentValue?.dataSourceQueryId;
    // const { filters = '{}', ...existingQueryParams } = this._activatedRoute?.snapshot?.queryParams || {};
    // const { columnSearch, ...filterJson } = (JSON.parse(filters) || {})[dsqId] || {};
    // const newColumnSearch = this.getFilterPayload(filterSettings);
    // if (newColumnSearch.length > 0) {
    //   filterJson.columnSearch = this.getFilterPayload(filterSettings);
    // } else {
    //   delete filterJson.columnSearch;
    // }

    // if (Object.keys(filters[dsqId] || {}).length == 0) {
    //   this._router
    //     .navigate([], {
    //       queryParams: {
    //         ...existingQueryParams
    //       }, // Clear all query params
    //       queryParamsHandling: null
    //     })
    //     .then(() => {
    //       this.fetchAPIData();
    //     });
    // } else {
    //   this._router
    //     .navigate([], {
    //       queryParams: {
    //         filters: JSON.stringify({
    //           [dsqId]: {
    //             ...filterJson
    //           }
    //         })
    //       },
    //       queryParamsHandling: 'merge'
    //     })
    //     .then(() => {
    //       this.fetchAPIData();
    //     });
    // }
  }

  private onClearFilter(field: string) {
    if (this.filterSettings?.columns?.length) {
      this.filterSettings.columns = this.filterSettings.columns.filter((column: any) => column.field !== field);
    }
    this.handleFilterAPICall(this.filterSettings);
  }

  private onFilter(currentFilterObject: any) {
    const filterCol = this.filterSettings?.columns?.find((col: any) => col?.field === currentFilterObject?.field);
    if (currentFilterObject?.value) {
      if (filterCol) {
        filterCol.operator = currentFilterObject?.operator;
        filterCol.value = currentFilterObject?.value;
      } else {
        this.filterSettings.columns.push({
          field: currentFilterObject?.field,
          operator: currentFilterObject?.operator,
          value: currentFilterObject?.value
        });
      }
    } else if (filterCol) {
      this.filterSettings.columns = this.filterSettings.columns.filter(
        (col: any) => col.field !== currentFilterObject?.field
      );
    }
    this.handleFilterAPICall(this.filterSettings);
  }

  dataBound(): void {
    if (this.isInitialDataBound && !this.isInDesigningMode) {
      const grid = this.gridInstance?.grid || this.gridInstance;
      const records = grid?.getCurrentViewRecords();
      if (records?.length > 0) {
        this.isInitialDataBound = false;
        setTimeout(() => grid.selectRow(0), 100);
      }
    }
  }

  rowSelected(args: RowSelectEventArgs): void {
    this.emitComponentValue({
      data: args?.data,
      rowIndex: args?.rowIndex
    })
  }

  emitComponentValue(value: any) {
    this.activeRow = (value?.data as any)?.taskData ?? value?.data;
    this._value = TabSdk.util.cloneAndUpdateComponentValue(this._value, {
      ...this._value.componentValue,
      selectedRow: { ...value.data }
    });

    TabSdk.store.addScreenContext(
      this.currentAppObject?.SystemDBTableName,
      { activeRowsData: { ...value.data, rowIndex: value?.rowIndex } }
    );

    this.valueChange.emit(this._value);
  }

  recordDoubleClick(args: any) {
    const configuredEvent =
      this._value.component?.EventActionConfiguration?.componentValue?.componentBasedEventConfig?.filter(
        (elem: any) => elem?.container?.event && elem?.container?.event === 'Record Double Click'
      );

    if (configuredEvent && configuredEvent?.length) {
      const configuredAction = configuredEvent[0]?.container?.actionConfigScreen?.componentValue;
      if (configuredAction) {
        this.executeAction(configuredAction?.configScreen?.componentValue, configuredAction?.action?.action, args?.rowData);
      }
    }
  }

  rowDrop(args: any) {
    this.executeGridExtensionEvent('rowDrop', args);
    if (
      !this.activeRow ||
      this.activeRow[this.currentPrimaryKeyField] !== args?.data?.[0]?.[this.currentPrimaryKeyField]
    ) {
      this.activeRow = args?.data?.[0]?.taskData ?? args?.data;
    }
  }

  expanded(args: any) {
    if (!this.activeRow || this.activeRow[this.currentPrimaryKeyField] !== args?.data?.[this.currentPrimaryKeyField]) {
      this.activeRow = args?.data?.taskData ?? args?.data;
    }
  }

  collapsed(args: any) {
    if (this.activeRow && this.activeRow[this.currentPrimaryKeyField] === args?.data?.[this.currentPrimaryKeyField]) {
      delete this.activeRow;
    }
  }

  private refreshGrid = () => {
    // Static Mode: Data is static, no need to refresh from API
    if (this.isStaticMode) {
      this.showStaticModeWarning('Data refresh');
      return;
    }

    this.fetchAPIData();
  };

  actionBegin(args: any) {
    this.executeGridExtensionEvent('actionBegin', args);

    // Static Mode Guard: Block CRUD operations
    if (this.isStaticMode && (args.requestType === 'save' || args.requestType === 'delete' || args.requestType === 'add')) {
      args.cancel = true;
      this.showStaticModeWarning('CRUD operations');
      return;
    }

    if (args.requestType === 'save') {
      args.cancel = true;
      this.getFormatedDataForSave(args.data);
      if (args.action === 'add') {
        this.inlineInsertData(args.data);
      } else if (args.action === 'edit') {
        if (JSON.stringify(args.previousData) !== JSON.stringify(args.data)) {
          this.inlineUpdateData(args.data);
        } else {
          this.refreshGrid();
        }
      }
    }
  }

  getFormatedDataForSave(data: any) {
    this.gridColumns.forEach((col: any) => {
      if (col?.type === 'date' && data[col?.field]) {
        data[col.field] = moment(data[col.field]).format('YYYY-MM-DD');
      } else if (col?.type === 'datetime' && data[col?.field]) {
        data[col.field] = moment(data[col.field]).format('YYYY-MM-DDTHH:mm:ssZ');
      }
    });
  }

  async cellSave(args: any) {
    if (args.previousValue !== args.value && this.currentAppObject?.ID && this.currentPrimaryKeyField) {
      if (args?.column?.type === 'date' || args?.column?.type === 'datetime') {
        args.value = moment(args.value).format();
      }

      const res = await TabSdk.crud.update(
        {
          appObjectIdOrName: this.currentAppObject.ID,
          data: this.buildUpdatePayload(args.rowData, args.column.field, args.value),
          criteria: { [this.currentPrimaryKeyField]: args.rowData[this.currentPrimaryKeyField] }
        },
        {
          skipDefaultToast: true,
          headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component)
        }
      );

      if (res.StatusCode == '200') {
        TabSdk.ui.showSuccess({ message: TranslationToken.Updated });
      } else {
        TabSdk.ui.showError({ message: res.Errors[0] });
      }
    }
  }

  /**
   * Builds the update payload for both flat and nested properties
   * @param rowData - The original row data
   * @param fieldPath - The field path (e.g., "SprintID.SquadID.SquadName" or "Title")
   * @param newValue - The new value to set
   * @returns Object with the proper structure for update
   */
  private buildUpdatePayload(rowData: any, fieldPath: string, newValue: any): any {
    const pathParts = fieldPath.split('.');

    // Handle flat properties (no dots)
    if (pathParts.length === 1) {
      return {
        [fieldPath]: newValue
      };
    }

    // Handle nested properties
    const rootProperty = pathParts[0];
    const nestedPath = pathParts.slice(1).join('.');

    // Get the current nested object or create empty structure
    const currentNestedObj = rowData[rootProperty] || {};

    // Create updated nested object based on existing data
    const updatedNestedObj = JSON.parse(JSON.stringify(rowData[rootProperty] || {}));
    TabSdk.util.setValueInJsonByPath(updatedNestedObj, nestedPath, newValue);

    return {
      [rootProperty]: updatedNestedObj
    };
  }

  private async inlineInsertData(data: any) {
    if (this.currentAppObject?.ID) {
      await TabSdk.crud.insert({
        appObjectIdOrName: this.currentAppObject.ID,
        data: data
      }, { headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component) });
      this.refreshGrid();
    }
  }

  private async inlineUpdateData(data: any) {
    if (this.currentAppObject?.ID && this.currentPrimaryKeyField) {
      const res = await TabSdk.crud.update(
        {
          appObjectIdOrName: this.currentAppObject.ID,
          data: data,
          criteria: { [this.currentPrimaryKeyField]: data[this.currentPrimaryKeyField] }
        },
        {
          skipDefaultToast: true,
          headers: this.helperFunctionService.buildComponentRequestHeaders(this._value.component)
        }
      );

      if (res.StatusCode == '200') {
        TabSdk.ui.showSuccess({ message: TranslationToken.Updated });
        this.refreshGrid();
      } else {
        TabSdk.ui.showError({ message: res.Errors[0] });
      }
    }
  }

  executeGridExtensionEvent(eventName: string, event: any) {
    if (this.currentGridExtension) {
      const params: any = {
        event: event,
        basicGrid: this.gridInstance,
        GridConfigurations: this._value.component,
        refresher: this.refreshGrid,
        parameters: this._value?.parameters,
        serviceList: {
          userPersonalizationHelper: this.userPersonalizationHelper,
          permissionService: this.permissionService,
          localStorageService: this._localStorageService,
          sessionStorageService: this._sessionStorageService,
          idbService: this.idbService,
          tabToasterService: this._tabToasterService,
          eventActionService: this.eventActionService,
          apphelper: this.apphelper
        }
      };

      if (this.currentGridExtension[eventName]) this.currentGridExtension[eventName](params);
    }
  }

  private configureActions() {
    const userDefinedActions = this._value.component?.EventActionConfiguration?.componentValue?.userDefinedActionConfig;
    if (userDefinedActions) {
      userDefinedActions.forEach((elem: any) => {
        this.mapAction(elem?.container);
      });
    }

    const componentBasedEventConfig =
      this._value.component?.EventActionConfiguration?.componentValue?.componentBasedEventConfig;
    if (componentBasedEventConfig) {
      componentBasedEventConfig.forEach((elem: any) => {
        if (
          elem?.container?.event === 'Record Double Click' &&
          elem?.container?.actionConfigScreen?.componentValue?.action?.action === 'InlineEditor'
        ) {
          this.editOptions.allowEditOnDblClick = true;
        }
      });
    }
  }

  private mapAction(container: any) {
    switch (container.spot) {
      case 'Context Menu':
        this.addTriggerInContextmenu(container);
        break;
    }
  }

  addTriggerInContextmenu(container: any) {
    let canProcessTrigger = false;
    const permission = container?.triggerComponentConfigScreen?.componentValue?.permissionScreen?.componentValue;
    const isPermissionConfigured = permission && permission?.permissionType && permission.permissionType !== '';
    if (isPermissionConfigured) {
      switch (PermissionType[permission?.permissionType] as any) {
        case 1:
          canProcessTrigger = this.permissionService?.doesHaveConfiguredPermission(permission?.viewablePermission);
          break;

        case 2:
          canProcessTrigger = this.permissionService?.doesHaveDataAccessPermission(
            permission?.accessType,
            permission?.appObjectId
          );
          break;
      }
    }

    if (!isPermissionConfigured || canProcessTrigger) {
      const triggerComponentConfig = container?.triggerComponentConfigScreen?.componentValue;
      const userDefinedActionConfig = container?.actionConfigScreen?.componentValue;
      let component: any = {};
      switch (triggerComponentConfig?.triggerComponent) {
        case 'button':
          component = this.getComponentJson(triggerComponentConfig, userDefinedActionConfig);
          break;

        case 'customJson':
          component = TabSdk.util.safeJsonParse(triggerComponentConfig?.customJson?.componentValue);
          break;

        case 'dropdown':
          component = this.getComponentJson(
            triggerComponentConfig?.configScreen?.componentValue,
            userDefinedActionConfig
          );
          component.componentType = 'dropdown';
          component.triggerComponentConfig = triggerComponentConfig?.configScreen?.componentValue;
      }
      this.contextMenuItems.push(component);
    }
  }

  getComponentJson(config: any, userDefinedActionConfig: any) {
    return {
      target: '.e-content',
      text: config?.placeHolder,
      iconCss: config?.icon,
      id: config?.placeHolder,
      arg: {
        action: userDefinedActionConfig?.action?.action,
        configuration: userDefinedActionConfig?.configScreen?.componentValue
      }
    };
  }

  contextMenuOpen(args: any) {
    if ('cellIndex' in args?.rowInfo || 'rowIndex' in args?.rowInfo) {
      args.items.forEach(async (elem: any) => {
        if (
          elem?.componentType === 'dropdown' &&
          (elem?.triggerComponentConfig?.mode === 'dsq' || elem?.items?.length === 0)
        ) {
          elem.items = await this.getDropdownValues(
            elem?.triggerComponentConfig,
            args?.rowInfo?.rowData?.taskData ?? args?.rowInfo?.rowData
          );
        }
      });
      this.executeGridExtensionEvent('contextMenuOpen', args);
    } else {
      args.cancel = true;
    }
  }

  async getDropdownValues(config: any, rowData: any) {
    switch (config?.mode) {
      case 'custom':
        return config?.customData.map((elem: any) => ({
          text: elem?.label,
          id: elem?.value
        }));

      case 'dsq':
        const response: any = await firstValueFrom(
          this.helperFunctionService.getDSQData(config?.dataSourceQueryId, { ...rowData }, undefined, false, this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode))
        );
        if (response?.Result) {
          return response.Result.map((elem: any) => ({
            text: elem[config?.displayField],
            id: elem[config?.valueField],
            data: elem
          }));
        }
        return [];
    }
  }

  contextMenuClick(args: any) {
    this.isContextMenuOpen = false;
    this.executeGridExtensionEvent('contextMenuClick', args);
    const { action, configuration } = args?.item?.arg ?? {};

    if (args.item.id?.startsWith('grid-action-')) {
      const action = args.item.id.replace('grid-action-', '');
      const commandArgs = {
        rowData: this.activeRow,
        commandColumn: { type: action.charAt(0).toUpperCase() + action.slice(1) }
      };

      this.commandClick(commandArgs);

    } else {
      this.executeAction(configuration, action, args?.rowInfo?.rowData);
    }
  }

  private executeAction(config: any, action: any, rowData: any): void {
    if (!config || !action) return;
    TabSdk.util.handleEventAction({ ...config, action }, { ...rowData, currentComponent: this._value.component.key });
  }

  private async processGridExtension() {
    this.currentGridExtension = await this.helperFunctionService.processComponentExtension(
      this._value.component,
      ComponentType.Grid,
      this.extensionComponentRootInstance,
      ExtensionDependencies
    );
  }

  private exceuteFormioCustomEvent(name: string, data: any) {
    this.formioEvent.emit({ eventName: 'customEvent', data: { name, data } });
  }

  actionComplete(args: any) {
    if (args.requestType === 'filterAfterOpen') {
      args.filterModel.dlgObj.btnObj[0].disabled = true;

      const filterControls = args.filterModel.dlgDiv.querySelectorAll('.e-control');

      const enableButton = (eventArgs: any) => {
        const isNestedSupport = this._value.component?.Configuration?.componentValue?.allowNestedGridSupport;

        const gridRef = isNestedSupport
          ? (this.gridInstance.grid.filterModule.filterModule as any)
          : (this.gridInstance.filterModule.filterModule as any);

        const controls = gridRef.dlgDiv.querySelectorAll('.e-control');

        gridRef.dlgObj.btnObj[0].disabled = !(
          controls[0].ej2_instances[0].value != null &&
          controls[1].ej2_instances[0].value != null
        );
      };

      filterControls[0].ej2_instances[0].change = enableButton;
      filterControls[1].ej2_instances[0].change = enableButton;
    }

    // Emit grid state changes for static mode
    if (this.isStaticMode) {
      if (args.requestType === 'filtering') {
        this.emitFilterChange();
      } else if (args.requestType === 'sorting') {
        this.emitSortChange();
      } else if (args.requestType === 'grouping' || args.requestType === 'ungrouping') {
        this.emitGroupChange();
      }
    }
  }

  // Emit filter change event
  private emitFilterChange(): void {
    const filterSettings = (this.gridInstance as any).filterSettings;
    const filters: any[] = [];

    if (filterSettings && filterSettings.columns && Array.isArray(filterSettings.columns)) {
      filterSettings.columns.forEach((filterCol: any) => {
        if (filterCol.field) {
          filters.push({
            field: filterCol.field,
            operator: filterCol.operator || 'equal',
            value: filterCol.value
          });
        }
      });
    }

    this.gridFilterChange.emit(filters);
  }

  // Emit sort change event
  private emitSortChange(): void {
    const sortSettings = (this.gridInstance as any).sortSettings;
    const sorts: any[] = [];

    if (sortSettings && sortSettings.columns && Array.isArray(sortSettings.columns)) {
      sortSettings.columns.forEach((sortCol: any) => {
        if (sortCol.field) {
          sorts.push({
            field: sortCol.field,
            direction: sortCol.direction === 'Descending' ? 'desc' : 'asc'
          });
        }
      });
    }

    this.gridSortChange.emit(sorts);
  }

  // Emit group change event
  private emitGroupChange(): void {
    const groupSettings = (this.gridInstance as any).groupSettings;
    const groups: any[] = [];

    if (groupSettings && groupSettings.columns && Array.isArray(groupSettings.columns)) {
      groupSettings.columns.forEach((groupCol: any) => {
        const field = typeof groupCol === 'string' ? groupCol : groupCol.field || groupCol.name;
        if (field) {
          groups.push({ field });
        }
      });
    }

    this.gridGroupChange.emit(groups);
  }

  /**
   * Queries the cell information based on the grid's configuration.
   * 
   * This method will handle both QuickView and Lookup cell configurations.
   * 
   * @param args - The arguments containing cell and column information.
   */
  queryCellInfo(args: any) {
    // Handle QuickView configuration
    // If QuickView is enabled, this will add a class to the cell
    // and set a data attribute to store the row index for efficient data retrieval.
    this.handleQuickViewCellConfiguration(args);

    // Handle lookup configuration
    // If nested grid support is enabled and the column has a lookup data source,
    // this will retrieve the display value from the cache and set it as the cell's inner text.
    this.handleLookupCellConfiguration(args);
  }

  /**
   * Configures a grid cell for lookup display value retrieval.
   * 
   * If nested grid support is enabled and the column has a lookup data source,
   * this method will retrieve the display value from the cache and set it as the
   * cell's inner text.
   * 
   * @param args - The arguments containing cell and column information.
   */
  private handleLookupCellConfiguration(args: any) {
    const column = args.column;

    // If nested grid support is off or there is no lookup data source, return
    if (
      !this._value.component?.Configuration?.componentValue?.allowNestedGridSupport ||
      !column?.LookUpDetails ||
      !this.lookupCache.has(column.field)
    ) return;

    // Get the data object
    const data = args.data.taskData ?? args.data;

    // Simple nested access - split is fast enough
    const pathSegments = column.field.split('.');
    let currentValue = data;

    // Traverse the nested access path
    for (const segment of pathSegments) {
      currentValue = currentValue?.[segment];
      if (currentValue === undefined) break;
    }

    // O(1) lookup
    if (currentValue !== undefined) {
      // Retrieve the display value from the cache
      const displayValue = this.lookupCache.get(column.field)?.get(currentValue);
      if (displayValue) {
        // Set the cell's inner text
        args.cell.innerText = displayValue;
      }
    }
  }

  /**
   * Configures a grid cell for quick view functionality.
   * 
   * @param args - The arguments containing cell and column information.
   */
  private handleQuickViewCellConfiguration(args: any) {
    // Exit if quick view is not enabled
    if (!this._value.component?.Configuration?.componentValue?.enableQuickView) return;

    // Retrieve quick view configuration
    const quickViewConfig = this._value.component?.Configuration?.componentValue?.quickViewConfiguration;

    // Add 'quickview-enabled-cell' class if the column matches the target column for quick view
    if (args?.column?.field === quickViewConfig?.targetColumn) {
      args?.cell?.classList?.add('quickview-enabled-cell');
    }
  }

  // Add method to open context menu on button click
  onActionDotClick(event: MouseEvent, rowData: any) {
    const data = rowData;
    delete data?.column;

    event.preventDefault();
    event.stopPropagation();

    this.isContextMenuOpen = true;
    this.emitComponentValue({
      data: data,
      rowIndex: data?.index
    });

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const row = target.closest('tr');

    if (row) {
      // Simulate context menu on the row
      const contextMenuEvent = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: rect.left,
        clientY: rect.top + rect.height,
        button: 2
      });
      row.dispatchEvent(contextMenuEvent);
    }
  }

  // Add this method to handle view option clicks
  onViewOptionClick(rowData: any): void {
    const data = rowData;
    delete data?.column;

    this.emitComponentValue({
      data: data,
      rowIndex: data?.index
    });

    const commandArgs = {
      rowData: this.activeRow,
      commandColumn: { type: 'View' }
    };

    this.commandClick(commandArgs);
  }

  /**
   * Creates a new instance of the {@link Tooltip} component.
   */
  private createTooltipInstance(): void {
    if (!this.gridInstance?.element) return;

    const quickViewConfig = this._value.component?.Configuration?.componentValue?.quickViewConfiguration;

    const tooltipInstance = new Tooltip({
      target: '.quickview-enabled-cell',
      opensOn: 'Hover',
      position: 'TopCenter',
      width: quickViewConfig?.width || '400px',
      height: quickViewConfig?.height || '300px',
      showTipPointer: true,
      openDelay: 1000,
      afterOpen: (args: any) => this.onQuickViewAfterOpen(args),
      afterClose: (args: any) => this.onQuickViewAfterClose()
    });

    tooltipInstance.appendTo(this.gridInstance.element);
  }

  /**
   * Handles the event after the quick view tooltip opens.
   * 
   * @param args - The arguments containing event data and element reference.
   */
  private onQuickViewAfterOpen(args: any) {
    // Close any existing tooltip component to prevent multiple instances
    this.onQuickViewAfterClose();

    // Retrieve row information based on the target element
    const rowInfo = this.gridInstance.getRowInfo(args.target);

    // Exit if no row data is found
    if (!args?.element || !rowInfo?.rowData) {
      return;
    }

    // Create and inject the renderer component into the tooltip element
    this.createAndInjectRenderer(args.element, rowInfo.rowData);
  }

  /**
   * Handles the cleanup process after the quick view tooltip closes.
   */
  private onQuickViewAfterClose() {
    // Check if there is an active tooltip component reference
    if (this.activeTooltipComponentRef) {
      // Destroy the component to prevent memory leaks
      this.activeTooltipComponentRef.destroy();
      // Reset the reference to null indicating no active component
      this.activeTooltipComponentRef = null;
    }
  }

  private createAndInjectRenderer(tooltipElement: HTMLElement, rowData: any) {
    const quickViewConfig = this._value.component?.Configuration?.componentValue?.quickViewConfiguration;

    // Create TabFormRendererComponent
    const componentRef = this.extensionComponentRootInstance.createComponent(TabFormRendererComponent);

    // Configure component
    componentRef.instance.screenRenderingMode = ScreenRenderingMode.QuickViewMode;
    componentRef.instance.defaultLoaderWhileRendering = true;

    // Inject into tooltip
    const contentArea = tooltipElement.querySelector('.e-tip-content') || tooltipElement;
    contentArea.appendChild(componentRef.location.nativeElement);

    // Load form with row data
    componentRef?.instance?.rendererService?.loadFormWithSubmission(
      quickViewConfig?.screen,
      rowData
    );

    // Store reference for cleanup
    this.activeTooltipComponentRef = componentRef;
  }

  /**
   * Toggles a record as a favorite of the user.
   * 
   * @param {any} rowData - The row data containing the record ID.
   * @param {Event} event - The event to stop propagation.
   */
  public toggleFavorite(rowData: any, event: Event) {
    event?.stopPropagation();
    this._userFavoritesService.toggleFavorite(this.currentAppObject?.ID, this.getItemId(rowData));
  }

  /**
   * Checks if a record is a favorite of the user.
   * 
   * @param {any} rowData - The row data containing the record ID.
   * @returns {boolean} True if the record is a favorite of the user, otherwise false.
   */
  public isFavorite(rowData: any): boolean {
    return this._userFavoritesService.isFavorite(
      // App object ID
      this.currentAppObject?.ID,
      // Record ID
      this.getItemId(rowData)
    );
  }

  /**
   * Retrieves the item ID from the provided row data.
   * 
   * This method uses the current primary key field to extract the ID from the row data.
   * 
   * @param {any} rowData - The data object representing a row in the grid.
   * @returns {any} The ID of the item, or undefined if the primary key field is not found.
   */
  private getItemId(rowData: any): any {
    // Access the item ID using the primary key field
    return rowData?.[this.currentPrimaryKeyField];
  }

  /**
   * Subscribes to favorite changes and updates the grid data accordingly.
   * 
   * This method listens to the `favoriteStateChanged` observable and updates the `_isFavorite` property of a record based on its ID and the provided favorite state.
   * If the active view is the favorites view, it will refresh the data by fetching from the API.
   * Otherwise, it updates the in-memory grid data.
   */
  private subscribeToFavoriteChanges(): void {
    this._userFavoritesService.getFavoriteStateChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ appObjId, recordId, isFavorite }) => {
        // Only process if it's for our current app object
        if (appObjId === this.currentAppObject.ID) {
          // Update the favorite state of the record
          this.updateFavoriteState(recordId, isFavorite);
        }
      });
  }

  /**
   * Updates the favorite state of a record in the grid data.
   * 
   * This method updates the '_isFavorite' property of a record based on its ID and the provided favorite state.
   * If the active view is the favorites view, it will refresh the data by fetching from the API.
   * Otherwise, it updates the in-memory grid data.
   * 
   * @param {string} recordId - The ID of the record to update.
   * @param {boolean} isFavorite - The new favorite state to set.
   */
  private updateFavoriteState(recordId: string, isFavorite: boolean) {
    // Check if the active custom view is the favorites view
    if (this.activeCustomViewId === this._userFavoritesService.FAVORITES_KEY) {
      // Refresh data by fetching from API
      this.fetchAPIData();
    } else {
      // If nested grid support and client-side pagination are not enabled
      if (
        !this._value.component?.Configuration?.componentValue?.allowNestedGridSupport &&
        !this._value?.component?.Configuration?.componentValue?.isClientSidePagination
      ) {
        // Update favorite state in flat grid data
        this.gridData.Result = this.gridData.Result.map((item: any) => {
          if (item[this.currentPrimaryKeyField] === recordId) {
            item._isFavorite = isFavorite;
          }
          return item;
        });
      } else {
        // Update favorite state in hierarchical grid data
        this.gridData = this.gridData.map((item: any) => {
          if (item[this.currentPrimaryKeyField] === recordId) {
            item._isFavorite = isFavorite;
          }
          return item;
        });

        if (this._value.component?.Configuration?.componentValue?.allowNestedGridSupport) {
          // Expand the active row to reflect changes
          this.expandActiveRow();
        }
      }
    }
  }

  /**
   * Handles the download data click event.
   * 
   * This method downloads the current grid data in the requested format.
   * 
   * @param {string} type - The file type to download the data as. Valid values are 'CSV', 'PDF', and 'Excel'.
   */
  private async onDownloadData(type: ReportType) {
    this.gridInstance?.showSpinner();
    const fileName = this.currentAppObject.DisplayName.replace(/ /g, '_');
    const result = await this.getDownloadData();

    switch (type) {
      // Download as CSV
      case ReportType.CSV:
        this.gridInstance?.csvExport({
          dataSource: result,
          fileName: `${fileName}.csv`,
        });
        break;

      // Download as PDF
      case ReportType.Pdf:
        this.gridInstance?.pdfExport({
          dataSource: result,
          fileName: `${fileName}.pdf`,
          pageOrientation: 'Landscape',
          pageSize: 'A3',
          hierarchyExportMode: 'All',
        });
        break;

      // Download as Excel
      case ReportType.Excel:
        this.gridInstance?.excelExport({
          dataSource: result,
          fileName: `${fileName}.xlsx`,
        });
        break;
    }

    this.gridInstance?.hideSpinner();
  }

  /**
   * Fetches the data from the data source query for download.
   * The 'IsServerSidePagination' parameter is set to false to retrieve all the records.
   * The 'Pager' parameter is removed from the request parameters.
   * 
   * @returns {Promise<any[]>} A promise resolving to the data to be downloaded.
   */
  private async getDownloadData(): Promise<any[]> {
    const params = this.getDSQParams();
    delete params?.Pager;
    params['IsServerSidePagination'] = false;

    const response = await firstValueFrom(this.helperFunctionService.getDSQData(
      this._value.component?.Configuration?.componentValue?.dataSourceQueryId,
      this._value?.parameters,
      params,
      false,
      this.helperFunctionService.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode)
    ));

    return response?.Result ?? [];
  }

  /**
   * Configures the export button.
   * It adds the export button to the grid's toolbar and populates the export dropdown items.
   * The export dropdown items are based on the component's configuration.
   */
  private configureExportButton() {
    // Add the export button to the grid's toolbar
    this.toolbarItems.push({
      template: this.exportDropdownTemplate,
      align: 'Right'
    });

    const config = this._value.component?.Configuration?.componentValue;
    // Populate the export dropdown items based on the component's configuration
    if (config?.enableExcelExport) {
      // Add the Excel export option
      this.exportItems.push({ text: 'Excel', id: 'export-excel', iconCss: 'fa fa-file-excel' });
    }

    if (config?.enableCSVExport) {
      // Add the CSV export option
      this.exportItems.push({ text: 'CSV', id: 'export-csv', iconCss: 'fa fa-file-csv' });
    }

    if (config?.enablePDFExport) {
      // Add the PDF export option
      this.exportItems.push({ text: 'PDF', id: 'export-pdf', iconCss: 'fa fa-file-pdf' });
    }
  }

  /**
   * Handles the export button select event.
   * It calls the onDownloadData method based on the selected export type.
   * @param args - The event arguments.
   */
  public onExportSelect(args: any): void {
    switch (args.item.id) {
      // Export as Excel
      case 'export-excel':
        this.onDownloadData(ReportType.Excel);
        break;

      // Export as CSV
      case 'export-csv':
        this.onDownloadData(ReportType.CSV);
        break;

      // Export as PDF
      case 'export-pdf':
        this.onDownloadData(ReportType.Pdf);
        break;
    }
  }

  // ============================================================================
  // STATIC DATA MODE SUPPORT
  // ============================================================================

  /**
   * Initializes static data mode by auto-generating columns from data
   */
  private initializeStaticMode(): void {
    const staticData = this._value.component?.Configuration?.componentValue?.staticData;

    if (!staticData || !Array.isArray(staticData) || staticData.length === 0) {
      console.warn('Static mode enabled but no staticData provided or data is empty');
      this.gridColumns = [];
      this.gridData = [];
      this.isShowGrid = true;
      this.calculateAvailableSpace();
      return;
    }

    // Auto-generate columns from data structure
    this.gridColumns = this.generateColumnsFromStaticData(staticData);

    // Set grid data directly (Syncfusion handles static binding)
    this.gridData = staticData;

    // Show grid
    this.isShowGrid = true;
    this.calculateAvailableSpace();
  }

  /**
   * Auto-generates Syncfusion grid columns from static data array
   * Supports nested objects using dot notation (e.g., "User.Name")
   * Scans all rows to find all possible columns (handles null nested objects)
   * Optimized with Set for O(1) lookups and early exits
   */
  private generateColumnsFromStaticData(data: any[]): any[] {
    if (!data || data.length === 0) {
      return [];
    }

    // Use Set for O(1) lookup to track discovered field paths
    const discoveredPaths = new Set<string>();

    // Collect all unique field paths from all rows
    for (const row of data) {
      if (row && typeof row === 'object' && row !== null && !Array.isArray(row)) {
        this.collectFieldPaths(row, '', discoveredPaths);
      }
    }

    // Convert Set to sorted array of field paths
    const allFieldPaths = Array.from(discoveredPaths).sort();

    // Generate column config for each unique field path
    // Use full field path as header for better understanding (e.g., "WorkItemType.WorkItemTypeName")
    return allFieldPaths.map(fieldPath => {
      return {
        field: fieldPath,
        headerText: fieldPath,  // Use full path for clarity
        width: 150,
        type: 'string',
        allowFiltering: true,
        allowSorting: true,
        allowGrouping: true,
        allowReordering: true,
        allowResizing: true
      };
    });
  }

  /**
   * Recursively collects all field paths from an object into a Set
   * Optimized for fast lookups - skips already discovered paths
   * Uses dot notation for nested fields (e.g., "WorkItemType.WorkItemTypeName")
   */
  private collectFieldPaths(obj: any, parentPath: string, pathsSet: Set<string>): void {
    // Early exit if obj is null/undefined
    if (!obj || typeof obj !== 'object') return;

    for (const key of Object.keys(obj)) {
      const value = obj[key];
      const fieldPath = parentPath ? `${parentPath}.${key}` : key;

      // Check if value is a nested object (not null, not array, not date)
      // Note: typeof null === 'object' in JavaScript, so we explicitly check for null
      if (value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)) {

        // Recursively collect nested field paths (don't add parent path)
        this.collectFieldPaths(value, fieldPath, pathsSet);
      } else {
        // Add primitive field path to Set (includes null, undefined, and all primitives)
        // This ensures columns are generated even if the value is null
        pathsSet.add(fieldPath);
      }
    }
  }

  /**
   * Shows warning message for features not supported in static data mode
   */
  private showStaticModeWarning(featureName: string): void {
    TabSdk.ui.showWarning({
      message: `${featureName} is not supported in static data mode`
    });
  }
}